'use client';
import { useEffect, useRef, memo } from 'react';
import { Box, Flex } from '@televet/kibble-ui/chakra';
import * as echarts from 'echarts';
import { DownloadIcon } from './DownloadIcon';

interface EChartRendererProps {
  options: echarts.EChartsOption;
  height?: string;
  minHeight?: string;
}

function EChartRendererComponent({
  options,
  height = '550px',
  minHeight = '400px',
}: EChartRendererProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!chartRef.current || !options) {
      return;
    }

    // Check container dimensions using multiple methods
    let containerWidth = chartRef.current.clientWidth;
    let containerHeight = chartRef.current.clientHeight;

    // Fallback: try getBoundingClientRect
    if (containerWidth === 0 || containerHeight === 0) {
      const rect = chartRef.current.getBoundingClientRect();
      containerWidth = rect.width;
      containerHeight = rect.height;
    }

    // Fallback: try parent width if still 0
    if (containerWidth === 0 && chartRef.current.parentElement) {
      containerWidth = chartRef.current.parentElement.clientWidth;
    }

    if (containerWidth === 0 || containerHeight === 0) {
      return;
    }

    try {
      // Initialize chart with canvas renderer only if not already initialized
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current, 'light', {
          renderer: 'canvas',
        });
      }

      // Skip if somehow chart instance doesn't exist
      if (!chartInstance.current) {
        return;
      }

      // Auto-adjust legend position if title and left-positioned legend exist
      let adjustedOptions = options;

      // Check if we have a title (could be object or array)
      const hasTitle =
        options.title &&
        (Array.isArray(options.title) ? options.title.length > 0 : true);
      // Check if we have a left-positioned vertical legend without top positioning
      if (
        hasTitle &&
        options.legend &&
        !Array.isArray(options.legend) &&
        options.legend.left === 'left' &&
        options.legend.orient === 'vertical' &&
        !options.legend.top
      ) {
        // Push legend down to account for title height (typically ~40-50px)
        adjustedOptions = {
          ...options,
          legend: {
            ...options.legend,
            top: '10%',
          },
        };
      }

      chartInstance.current.setOption(adjustedOptions);
      // Force resize to ensure layout updates
      chartInstance.current.resize();
    } catch (error) {
      console.error('[EChartRenderer] Error initializing chart:', error);
    }

    // Handle window resize
    const handleResize = () => {
      chartInstance.current?.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [options]);

  // Cleanup ONLY on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  const handleDownload = () => {
    if (!chartInstance.current) return;
    const dataUrl = chartInstance.current.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `otto-chart-${Date.now()}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Box position="relative">
      <Box
        ref={chartRef}
        w="100%"
        h={height}
        minH={minHeight}
        display="block"
        overflow="hidden"
      />
      <Flex
        as="button"
        position="absolute"
        top="0"
        right="0"
        align="center"
        gap="4px"
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        px="6px"
        py="2px"
        fontSize="xs"
        color="gray.600"
        cursor="pointer"
        onClick={handleDownload}
        title="Download chart as PNG"
        _hover={{ bg: 'gray.50', color: 'gray.800' }}
      >
        <DownloadIcon />
        PNG
      </Flex>
    </Box>
  );
}

// Memoize the component to prevent unnecessary re-renders
// Only re-render if options, height, or minHeight actually change
export const EChartRenderer = memo(
  EChartRendererComponent,
  (prevProps, nextProps) => {
    // Deep comparison of options would be expensive, so we use a simpler approach:
    // Only re-render if height or minHeight change, or if options reference changes
    // The options comparison relies on ReactMarkdown providing stable references
    return (
      prevProps.height === nextProps.height &&
      prevProps.minHeight === nextProps.minHeight &&
      prevProps.options === nextProps.options
    );
  }
);
